import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";

const testimonials = [
  {
    quote:
      "Már az első alkalommal éreztem, hogy itt valóban figyelnek rám. Nyugodt környezet, profi hozzáállás és teljes kikapcsolódás.",
    name: "K. Eszter",
    detail: "Visszatérő vendég",
  },
  {
    quote:
      "A kezelés után sokkal könnyebbnek éreztem magam. Különösen tetszett, hogy minden részlet nyugodt és igényes volt.",
    name: "Sz. Gábor",
    detail: "Frissítő masszázs",
  },
  {
    quote:
      "Diszkrét, elegáns hely, kedves fogadtatás és nagyon alapos masszázs. Szívesen ajánlom.",
    name: "T. Anita",
    detail: "Svédmasszázs",
  },
];

const Testimonials = () => {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setIdx((p) => (p + 1) % testimonials.length);
    }, 7000);
    return () => clearInterval(interval);
  }, []);

  const current = testimonials[idx];

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="relative py-28 md:py-40 lg:py-48 bg-anthracite-950 overflow-hidden"
    >
      {/* Background number */}
      <div className="absolute top-20 right-0 font-serif text-[18rem] md:text-[26rem] text-white/[0.025] leading-none pointer-events-none select-none">
        03
      </div>
      {/* Burgundy radial */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_50%,rgba(122,46,54,0.10),transparent_50%)]" />

      <div className="max-w-[1500px] mx-auto px-6 md:px-10 lg:px-14 relative">
        <div className="grid grid-cols-12 gap-8 items-start">
          {/* Sidebar - label */}
          <div className="col-span-12 md:col-span-3 md:sticky md:top-32">
            <div className="flex items-center gap-4 mb-6">
              <span className="text-[10px] uppercase tracking-[0.4em] text-gold-500/80">
                03 — Vélemények
              </span>
            </div>
            <h2
              data-testid="testimonials-title"
              className="font-serif text-4xl md:text-5xl lg:text-6xl font-light text-cream leading-[1.02] tracking-[-0.02em]"
            >
              Vendégeink
              <span className="block italic gold-gradient-text mt-1">szavai.</span>
            </h2>
            <p className="mt-8 text-cream/50 font-light text-sm leading-relaxed border-l border-gold-500/30 pl-5">
              A valódi visszajelzés a legértékesebb. Köszönöm minden vendégemnek a
              megosztott pillanatokat.
            </p>
          </div>

          {/* Main quote */}
          <div className="col-span-12 md:col-span-9 md:pl-12 lg:pl-20 mt-8 md:mt-0">
            <div className="relative min-h-[360px] md:min-h-[420px]">
              {/* Decorative giant quote mark */}
              <div className="absolute -top-4 sm:-top-8 -left-1 sm:-left-3 md:-left-8 font-serif text-[7rem] sm:text-[10rem] md:text-[16rem] lg:text-[18rem] text-gold-500/15 leading-none pointer-events-none select-none">
                "
              </div>

              <AnimatePresence mode="wait">
                <motion.figure
                  key={idx}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                  data-testid={`testimonial-${idx}`}
                  className="relative pt-10 md:pt-12"
                >
                  <div className="flex gap-1.5 mb-6 md:mb-8">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-3.5 h-3.5 md:w-4 md:h-4 text-gold-500 fill-gold-500" strokeWidth={1} />
                    ))}
                  </div>

                  <blockquote className="font-serif italic text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-cream/95 leading-[1.25] font-light tracking-tight">
                    {current.quote}
                  </blockquote>

                  <figcaption className="mt-10 md:mt-12 pt-6 md:pt-8 border-t border-gold-500/20 flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <div className="font-serif text-xl md:text-2xl text-gold-500 italic">{current.name}</div>
                      <div className="text-[10px] uppercase tracking-[0.3em] md:tracking-[0.4em] text-cream/40 mt-1 md:mt-2">
                        {current.detail}
                      </div>
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.3em] md:tracking-[0.4em] text-cream/30 flex-shrink-0">
                      {String(idx + 1).padStart(2, "0")} <span className="text-cream/15">/</span> {String(testimonials.length).padStart(2, "0")}
                    </div>
                  </figcaption>
                </motion.figure>
              </AnimatePresence>
            </div>

            {/* Controls */}
            <div className="mt-10 flex items-center justify-between border-t border-white/10 pt-8">
              <div className="flex gap-3">
                <button
                  onClick={() => setIdx((p) => (p - 1 + testimonials.length) % testimonials.length)}
                  data-testid="testimonial-prev"
                  aria-label="Előző"
                  className="w-12 h-12 border border-gold-500/40 hover:bg-gold-500 hover:border-gold-500 group transition-all flex items-center justify-center"
                >
                  <ChevronLeft className="w-4 h-4 text-gold-500 group-hover:text-anthracite-950 transition-colors" strokeWidth={1.5} />
                </button>
                <button
                  onClick={() => setIdx((p) => (p + 1) % testimonials.length)}
                  data-testid="testimonial-next"
                  aria-label="Következő"
                  className="w-12 h-12 border border-gold-500/40 hover:bg-gold-500 hover:border-gold-500 group transition-all flex items-center justify-center"
                >
                  <ChevronRight className="w-4 h-4 text-gold-500 group-hover:text-anthracite-950 transition-colors" strokeWidth={1.5} />
                </button>
              </div>

              <a
                href="https://g.page/r/noblesse"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="google-reviews-link"
                className="inline-flex items-center gap-3 text-[10px] uppercase tracking-[0.35em] text-cream/50 hover:text-gold-500 transition-colors"
              >
                <Star className="w-3.5 h-3.5 text-gold-500" strokeWidth={1.5} fill="currentColor" />
                Google-vélemények
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
